/**
 * Holdings API
 */
import { apiClient } from './client'
import type { Holding, HoldingCreate, HoldingUpdate } from '../types'

const BASE_PATH = '/api/holdings'

export interface HoldingsListParams {
  accountId?: string
  portfolioId?: string
  tagIds?: string[]
}

export interface BulkOperationResult {
  success: boolean
  modified_count: number
  message: string
}

export type BulkTagAction = 'set' | 'add' | 'remove'

export const holdingsApi = {
  list: async (params?: HoldingsListParams): Promise<Holding[]> => {
    const queryParams: Record<string, string> = {}
    if (params?.accountId) queryParams.account = params.accountId
    if (params?.portfolioId) queryParams.portfolio = params.portfolioId
    if (params?.tagIds?.length) queryParams.tags = params.tagIds.join(',')
    const response = await apiClient.get<Holding[]>(BASE_PATH, { params: queryParams })
    return response.data
  },

  get: async (holdingId: string): Promise<Holding> => {
    const response = await apiClient.get<Holding>(`${BASE_PATH}/${holdingId}`)
    return response.data
  },

  create: async (data: HoldingCreate): Promise<Holding> => {
    const response = await apiClient.post<Holding>(BASE_PATH, data)
    return response.data
  },

  update: async (holdingId: string, data: HoldingUpdate): Promise<Holding> => {
    const response = await apiClient.put<Holding>(`${BASE_PATH}/${holdingId}`, data)
    return response.data
  },

  delete: async (holdingId: string): Promise<void> => {
    await apiClient.delete(`${BASE_PATH}/${holdingId}`)
  },

  bulkUpdateTags: async (
    holdingIds: string[],
    action: BulkTagAction,
    tagIds: string[]
  ): Promise<BulkOperationResult> => {
    const response = await apiClient.patch<BulkOperationResult>(
      `${BASE_PATH}/bulk/tags`,
      { holding_ids: holdingIds, action, tag_ids: tagIds }
    )
    return response.data
  },

  bulkUpdatePortfolio: async (
    holdingIds: string[],
    portfolioId: string
  ): Promise<BulkOperationResult> => {
    const response = await apiClient.patch<BulkOperationResult>(
      `${BASE_PATH}/bulk/portfolio`,
      { holding_ids: holdingIds, portfolio_id: portfolioId }
    )
    return response.data
  },

  bulkDelete: async (holdingIds: string[]): Promise<BulkOperationResult> => {
    const response = await apiClient.delete<BulkOperationResult>(
      `${BASE_PATH}/bulk`,
      { data: { holding_ids: holdingIds } }
    )
    return response.data
  },
}
